import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import InputText from "../input-text/InputText"
import { InputTextProps } from "../input-text/input-text.props"
import "./InputTextSuggestions.css"
import { useEffectClickOutside } from "src/shared/hooks"
import Suggestions from "./Suggestions/Suggestions"
import { computePosition, flip, shift } from "@floating-ui/react"
import { debounce } from "src/utils/lodash"
import ArrowSvg from "src/shared/icons/arrowDropdown"

export interface InputTextSuggestion {
    title: string
    subtitle?: string
    photoUrl?: string
    id: string
    displaySuggestions?: boolean
}
interface PropsInputTextSuggestions {
    inputTextConfig: InputTextProps
    value: string | InputTextSuggestion | null
    updateInputTextValueOnSelectItem?: boolean
    onChange: (value: string | InputTextSuggestion) => void
    suggestions: string[] | InputTextSuggestion[]
    className?: string
    suggestionsListMaxHeight?: string
    suggestionsListWidth?: string
    lazy?: boolean
    onBottomReached?: (value: string) => void
    onTextChange?: (value: string) => void
    displaySuggestions?: boolean
    regexp?: RegExp
    withChevron?: boolean
}

const InputTextSuggestions = ({
    value,
    onChange,
    suggestions,
    inputTextConfig,
    className,
    lazy = false,
    onTextChange,
    updateInputTextValueOnSelectItem = true,
    onBottomReached,
    regexp,
    displaySuggestions = true,
    withChevron = false,
}: PropsInputTextSuggestions) => {
    const [mode, setMode] = useState<"search" | "select">("search")
    const [inputValue, setInputValue] = useState(typeof value === "string" ? value : value?.title || "")

    const [sowSuggestions, setSowSuggestions] = useState(false)

    const inputTextRef = useRef<HTMLDivElement>(null)
    const suggestionsRef = useRef<HTMLUListElement>(null)

    useEffectClickOutside(inputTextRef, () => {
        setSowSuggestions(false)
    })

    // acomodar la lista flotante del dropdown
    useEffect(() => {
        if (!inputTextRef.current || !suggestionsRef.current) {
            return
        }
        computePosition(inputTextRef.current, suggestionsRef.current, {
            placement: "bottom-start",
            middleware: [flip(), shift()],
        }).then(({ x, y }) => {
            const style = suggestionsRef?.current?.style || {}
            Object.assign(style, {
                top: `${y + 5}px`,
                left: `${x}px`,
                width: `${inputTextRef.current?.getBoundingClientRect().width || 0}px`,
            })
        })
    }, [sowSuggestions, suggestions])

    const filteredSuggestions = useMemo(() => {
        if (!displaySuggestions) {
            return suggestions
        }

        const inputValueTransformed = inputValue?.toLowerCase().trim()

        return suggestions.filter((option) =>
            typeof option === "string"
                ? option
                    .toLowerCase()
                    .trim()
                    .includes(inputValueTransformed || "")
                : option.title
                    .toLowerCase()
                    .trim()
                    .includes(inputValueTransformed || "")
        )
    }, [inputValue, sowSuggestions, suggestions, displaySuggestions])

    const openSuggestions = () => {
        setSowSuggestions(true)
    }

    const toggleSuggestions = () => {
        setSowSuggestions((prev) => !prev)
    }

    const handleBottomReached = () => {
        onBottomReached?.(inputValue || "")
    }

    const handleBlur = useCallback(
        debounce((e: any) => (mode !== "select" ? inputTextConfig?.onBlur?.(e) : undefined), 500),
        [mode]
    )

    const timerRef = useRef<NodeJS.Timer>()
    const [isLoading, setisLoading] = useState(false)

    useEffect(() => {
        setisLoading(false)
    }, [suggestions])

    const handleChange = (v: string) => {
        clearTimeout(timerRef.current)
        setisLoading(lazy ? true : false)

        if(!lazy) {
            onTextChange?.(v) && suggestions.length && setSowSuggestions(true)
            if (!v) {
                onChange("")
            }
            return
        }

        timerRef.current = setTimeout(() => {
            setMode("search")
            lazy && onTextChange?.(v) && suggestions.length && setSowSuggestions(true)
            if (!v) {
                onChange("")
            }
        }, 500)
    }

    useEffect(() => {
        setInputValue(typeof value === "string" ? value : value?.title || "")
    }, [value])
    

    return (
        <div
            className={className ? className : "inputText-suggestions"}
            style={{ position: "relative" }}
            ref={inputTextRef}
        >
            <Suggestions
                onBottomReached={handleBottomReached}
                visible={!!(sowSuggestions && filteredSuggestions.length)}
                ref={suggestionsRef}
                onChange={(v) => {
                    onChange(v)
                    setMode("select")
                }}
                setInputValue={setInputValue}
                setSowSuggestions={setSowSuggestions}
                suggestions={filteredSuggestions}
                updateInputTextValueOnSelectItem={updateInputTextValueOnSelectItem}
            />
            <InputText
                onClick={openSuggestions}
                value={inputValue}
                onChange={(e) => {
                    if(!regexp) {
                        setInputValue(e.target.value)
                        handleChange(e.target.value)
                    }
                    if(regexp && regexp.test(e.target.value)) {
                        setInputValue(e.target.value)
                        handleChange(e.target.value)
                        return
                    }
                }}
                loader={isLoading}
                {...inputTextConfig}
                onBlur={handleBlur}
                endIcon={
                    withChevron ? (
                        <ArrowSvg
                            onClick={toggleSuggestions}
                            style={{
                                transform: `rotate(${sowSuggestions ? "-180deg" : "0deg"})`,
                            }}
                        />
                    ) : undefined
                }
            />
        </div>
    )
}

export default InputTextSuggestions
